import { useDeferredValue, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, GraduationCap, MessageCircleQuestion, UserRound, type LucideIcon } from 'lucide-react';
import { openAssistant } from '@/help/assistant-store';
import { useAuth } from '@/auth/AuthProvider';
import { Modal, TextInput } from '@/components/ui';
import { useT } from '@/i18n/context';
import { visibleNavItems } from '@/layouts/mobile-nav';
import { api, qs } from '@/lib/api';
import type { Institution, PlatformAccountsPage, StudentRow } from '@/lib/types';

interface Hit { key: string; to: string; label: string; sub: string; icon: LucideIcon }

/**
 * البحثُ العامّ `Ctrl+K`/`⌘K` (27 سبتمبر 2026): شاشاتُ قائمتك، ثم ما **تملك رؤيتَه أصلاً** من المسارات
 * القائمة نفسِها — طلبةُ نطاقك من `GET /students` (المصفّى في الخادم بالإسناد والروستر)، والحساباتُ من
 * `GET /platform/accounts` لمن يملك `user.view_all`، والمؤسساتُ لمن يملك `institution.view_all`.
 * لا مسارَ جديداً يقرأ ما لا يُرى. الأسهمُ تتنقّل وEnter يفتح.
 */
export function CommandPalette({ onClose }: { onClose: () => void }) {
  const { roles, me, can, institutionId } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const listId = useId();
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const q = useDeferredValue(term.trim());

  const students = useQuery({
    queryKey: ['palette', 'students', institutionId],
    queryFn: () => api.get<{ items: StudentRow[] }>('/students'),
    enabled: !!institutionId && can('student.view'),
    staleTime: 60_000,
  });
  const accounts = useQuery({
    queryKey: ['palette', 'accounts', q],
    queryFn: () => api.get<PlatformAccountsPage>(`/platform/accounts${qs({ q, limit: 8 })}`, { institutionId: null }),
    enabled: q.length >= 2 && can('user.view_all'),
    staleTime: 30_000,
  });
  const institutions = useQuery({
    queryKey: ['palette', 'institutions'],
    queryFn: () => api.get<Institution[]>('/institutions', { institutionId: null }),
    enabled: can('institution.view_all'),
    staleTime: 60_000,
  });

  const hits = useMemo<Hit[]>(() => {
    const has = (s: string | null | undefined) => !!s && s.toLowerCase().includes(q.toLowerCase());
    const out: Hit[] = [];
    const seen = new Set<string>();
    for (const i of visibleNavItems({ roles, studentKind: me?.student?.kind ?? null })) {
      const label = t(i.labelId);
      if (seen.has(i.to) || (q && !has(label))) continue;
      seen.add(i.to);
      out.push({ key: `nav:${i.to}`, to: i.to, label, sub: 'شاشة', icon: i.icon });
    }
    if (!q) return out.slice(0, 8);
    for (const s of students.data?.items ?? []) {
      if (has(s.student.fullName) || has(s.student.referenceCode)) out.push({ key: `st:${s.student.id}`, to: `/app/students/${s.student.id}`, label: s.student.fullName, sub: `طالب · ${s.student.referenceCode}`, icon: GraduationCap });
    }
    for (const a of accounts.data?.items ?? []) {
      out.push({ key: `ac:${a.id}`, to: `/platform/accounts/${a.id}`, label: a.fullName, sub: `حساب · ${a.clientAccount?.referenceCode ?? a.email}`, icon: UserRound });
    }
    for (const i of institutions.data ?? []) {
      if (has(i.name)) out.push({ key: `in:${i.id}`, to: `/platform/institutions/${i.id}`, label: i.name, sub: 'مؤسسة', icon: Building2 });
    }
    return out.slice(0, 20);
  }, [q, roles, me?.student?.kind, t, students.data, accounts.data, institutions.data]);

  const cur = Math.min(active, Math.max(0, hits.length - 1));
  const open = (h: Hit | undefined) => { if (!h) return; onClose(); navigate(h.to); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((cur + 1) % Math.max(1, hits.length)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((cur - 1 + hits.length) % Math.max(1, hits.length)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (hits.length === 0 && q) { onClose(); openAssistant(q); } else open(hits[cur]); }
  };

  return (
    <Modal title="ابحث في رفرفة" onClose={onClose}>
      <div className="stack gap-3">
        <TextInput
          label="اسم شاشة، أو طالب، أو حساب"
          hint="يبحث فيما تملك رؤيتَه وحده — الأسهمُ تتنقّل وEnter يفتح."
          type="search" value={term} autoFocus
          onChange={(e) => { setTerm(e.target.value); setActive(0); }}
          onKeyDown={onKey}
          role="combobox" aria-expanded="true" aria-controls={listId}
          aria-activedescendant={hits[cur] ? `${listId}-${cur}` : undefined}
        />
        <ul id={listId} role="listbox" className="palette" aria-label="النتائج">
          {hits.map((h, i) => (
            <li key={h.key} id={`${listId}-${i}`} role="option" aria-selected={i === cur}
              className={`palette__item${i === cur ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)} onClick={() => open(h)}>
              <h.icon size={18} aria-hidden />
              <span className="grow truncate">{h.label}</span>
              <span className="caption">{h.sub}</span>
            </li>
          ))}
          {hits.length === 0 && (
            <li className="palette__item is-active palette__ask" role="option" aria-selected="true" data-testid="palette-ask" onClick={() => { onClose(); openAssistant(q); }}>
              <MessageCircleQuestion size={18} aria-hidden />
              <span className="grow">{`لا شاشةَ باسم «${q}» — اسأل المساعد بكلماتك`}</span>
            </li>
          )}
        </ul>
      </div>
    </Modal>
  );
}
